import { createApiClient } from '../../api/client';
import type { Bank } from './schema';

/**
 * external-bank talks to the PISP microservice's REST API (`/api/v1/*`).
 *
 * Auth note: these endpoints are public from Toqio's perspective — the
 * Toqio session token is not required. The BE needs the *PISP JWT* (the
 * token lum-web received in the deep-link URL, forwarded as
 * `init.input.jwt` on Step 1 and as `init.input.toqio` on Step 2). Per
 * the BE contract this JWT travels as a request **body** field (`jwt`),
 * not as an `Authorization` header. The same JWT is decoded client-side
 * for display only (see `jwt.ts`).
 *
 * Base URL is `VITE_API_URL` from `apps/forms-host/.env.<mode>`. Empty
 * in dev / unit tests so MSW (relative-path handlers) intercepts; per-env
 * builds point it at `https://api.<env>.toq.io/custom-flows`.
 *
 * All PISP endpoints live under `/api/v1/pisp`. We only ever send the
 * required body fields — optional ones (country, currency, redirectType,
 * returnUrl…) are sourced from the JWT or BE defaults, so we omit them.
 */
const API_BASE = import.meta.env.VITE_API_URL ?? '';
export const BANKS_PATH = '/api/v1/pisp/banks';
export const PAYMENTS_PATH = '/api/v1/pisp/payments';
export const REVENUE_ID_PATH = '/api/v1/pisp/payments/revenue-id';

const apiClient = () => createApiClient(API_BASE);

// --- /api/v1/banks ---------------------------------------------------------

type ListBanksResponse = {
  code: string;
  description?: string | null;
  termsUrl?: string;
  banks?: Bank[];
};

export type BootstrapResponse = {
  code: string;
  description: string | null;
  termsUrl: string;
  banks: Bank[];
};

export const fetchBootstrap = async (
  jwt: string,
  country?: string,
): Promise<BootstrapResponse> => {
  // `country` defaults to ES on the BE and `currency` is read from the JWT —
  // both optional, so we only send `country` when explicitly provided.
  const { data } = await apiClient().post<ListBanksResponse>(BANKS_PATH, {
    jwt,
    ...(country ? { country } : {}),
  });
  return {
    code: data.code,
    description: data.description ?? null,
    termsUrl: data.termsUrl ?? '',
    banks: data.banks ?? [],
  };
};

// --- /api/v1/payments ------------------------------------------------------

type CreatePaymentApiResponse = {
  code: string;
  description?: string | null;
  stage?: string;
  paymentId?: string;
  redirectUrl?: string;
  // Decoupled push-authorisation scenario (SEB-1713/1714): a successful
  // create with no `redirectUrl`; the payer authorises via a push
  // notification on their device. Nullable — absent on the redirect path.
  authorizationStatus?: string | null;
};

export type CreatePaymentResponse = {
  code: string;
  description: string | null;
  redirectUrl?: string;
  paymentId?: string;
  authorizationStatus?: string | null;
};

export const createPayment = async (
  jwt: string,
  bank: Bank,
  termsAccepted: boolean,
  accountIdentifiers: Record<string, string> = {},
): Promise<CreatePaymentResponse> => {
  // 201 Created on success; axios resolves any 2xx, so no status guard here.
  // `accountIdentifiers` carries the values the payer entered for the bank's
  // `inputRequired` keys (SEB-1664). Omit the field entirely when empty so
  // banks that require nothing keep the previous request shape (the BE drops
  // an absent map; an empty object would be a needless contract change).
  const { data } = await apiClient().post<CreatePaymentApiResponse>(PAYMENTS_PATH, {
    jwt,
    bankId: bank.id,
    bankName: bank.name,
    bankCountry: bank.country,
    termsAndConditionsAccepted: termsAccepted,
    ...(Object.keys(accountIdentifiers).length > 0 ? { accountIdentifiers } : {}),
  });
  return {
    code: data.code,
    description: data.description ?? null,
    redirectUrl: data.redirectUrl,
    paymentId: data.paymentId,
    authorizationStatus: data.authorizationStatus ?? null,
  };
};

// --- /api/v1/pisp/payments/revenue-id --------------------------------------

export type ResolveRevenueIdResponse = {
  revenueId: string;
};

/**
 * After Volt redirects the payer back, resolve the lum-wallet `revenueId`
 * so we can hand it to the merchant's callback page. `voltToken` is the
 * raw base64url token Volt appended to the redirect URL (`{ id,
 * paymentReference, status }` once decoded); we forward it untouched with
 * its HMAC signature + timestamp so the BE can verify and look up the
 * payment. The PISP JWT travels in the body too (carries `customerId`).
 *
 * Read-only and HMAC-gated. On success the BE returns a bare
 * `{ revenueId }` (no `code`/envelope); any error is the standard error
 * envelope on a non-2xx status, which axios surfaces as a thrown error.
 *
 * Status updates do NOT flow through here — they reach lum-wallet
 * server-to-server via Volt's `/payment-callbacks/volt` webhook.
 */
export const resolveRevenueId = async (
  jwt: string,
  voltToken: string,
  voltSignature: string,
  voltTimestamp: string,
): Promise<ResolveRevenueIdResponse> => {
  const { data } = await apiClient().post<ResolveRevenueIdResponse>(
    REVENUE_ID_PATH,
    { jwt, voltToken, voltSignature, voltTimestamp },
  );
  return { revenueId: data.revenueId };
};
